import React from 'react';
import { WxbAlert } from '../Alert/Alert';
import { WxbActionMenu } from '../ActionMenu/ActionMenu';
import { WxbButton } from '../Button/Button';
import { WxbEmpty } from '../Empty/Empty';
import { WxbDataTable } from '../DataTable/DataTable';
import { WxbDrawer } from '../Drawer/Drawer';
import { WxbIcon } from '../Icon/Icon';
import { WxbSegmented } from '../Segmented/Segmented';
import { WxbSlider } from '../Slider/Slider';
import { WxbTag } from '../Tag/Tag';
import { WxbTooltip } from '../Tooltip/Tooltip';
import { GanttV5CanvasLayers } from './GanttV5CanvasLayers';
import GanttV5ContextMenu from './GanttV5ContextMenu';
import SelectionDrawerV5 from './SelectionDrawerV5';
import { GanttV5Sidebar } from './GanttV5Sidebar';
import {
  applyGanttV5TaskSpans,
  createGanttV5InteractionState,
  createGanttV5MoveDraft,
  createGanttV5Transaction,
  ganttV5InteractionReducer,
} from './interaction';
import { buildGanttV5Model, getGanttV5VisibleRows } from './model';
import { GANTT_V5_HEADER_HEIGHT, type GanttV5Viewport } from './rendering';
import {
  buildGanttV5Ticks,
  clampGanttV5Zoom,
  createGanttV5TimeScale,
  getGanttV5FitPixelsPerHour,
  inferGanttV5TimeRange,
} from './timeScale';
import type {
  GanttV5EngineProps,
  GanttV5ContextMenuContext,
  GanttV5MoveDraft,
  GanttV5TaskSpan,
  GanttV5Transaction,
  GanttV5ViewMode,
} from './types';
import './GanttV5.css';

const DEFAULT_ROW_HEIGHT = 34;
const DEFAULT_SIDEBAR_WIDTH = 280;
const DEFAULT_PIXELS_PER_HOUR = 6;

interface PointerSession {
  pointerId: number;
  taskId: string;
  edge: 'start' | 'body' | 'end';
  startClientX: number;
  draft: GanttV5MoveDraft | null;
  affectedTaskIds: string[];
  groupId?: string;
}

interface EngineViewportSize {
  width: number;
  height: number;
}

const classNames = (...values: Array<string | false | undefined>) => values.filter(Boolean).join(' ');

const buildHistoryScopeKey = (
  tasks: GanttV5EngineProps['tasks'],
  timelineOriginDate: string | undefined,
): string => {
  let hash = 2166136261;
  for (const task of tasks) {
    for (let index = 0; index < task.id.length; index += 1) {
      hash ^= task.id.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    hash ^= 31;
    hash = Math.imul(hash, 16777619);
  }
  return `${timelineOriginDate ?? ''}:${tasks.length}:${hash >>> 0}`;
};

export const WxbGanttEngineV5: React.FC<GanttV5EngineProps> = ({
  tasks,
  groups = [],
  dependencies = [],
  links = [],
  timeRange,
  timelineOriginDate,
  viewMode: controlledViewMode,
  rowHeight = DEFAULT_ROW_HEIGHT,
  sidebarWidth = DEFAULT_SIDEBAR_WIDTH,
  connectionVisibility = 'all',
  autoFitOnScopeChange = false,
  readOnly = false,
  className,
  onTaskClick,
  onTaskDoubleClick,
  onDeleteTask,
  onTaskMove,
  onTasksMove,
  onGroupMove,
  onTaskResize,
  onGroupToggle,
  onSelectionChange,
  onMutationPendingChange,
  contextMenuItems,
  onContextMenuAction,
  toolbarLeading,
  toolbarActions,
  selectionActions,
  emptyDescription = '当前范围内没有可显示的任务',
  onLayerRender,
}) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const scrollSurfaceRef = React.useRef<HTMLDivElement>(null);
  const pointerSessionRef = React.useRef<PointerSession | null>(null);
  const frameRef = React.useRef<number | null>(null);
  const resizeFrameRef = React.useRef<number | null>(null);
  const retryRef = React.useRef<null | (() => Promise<void>)>(null);
  const historyScopeKey = React.useMemo(
    () => buildHistoryScopeKey(tasks, timelineOriginDate),
    [tasks, timelineOriginDate],
  );
  const historyScopeRef = React.useRef(historyScopeKey);
  const defaultCollapseScopeRef = React.useRef('');
  const autoFitScopeRef = React.useRef('');
  const [interaction, dispatch] = React.useReducer(
    ganttV5InteractionReducer,
    undefined,
    () => createGanttV5InteractionState(new Set(groups.filter((group) => group.collapsed).map((group) => group.id))),
  );
  const [displayTasks, setDisplayTasks] = React.useState(tasks);
  const [viewMode, setViewMode] = React.useState<GanttV5ViewMode>(controlledViewMode ?? 'day');
  const [pixelsPerHour, setPixelsPerHour] = React.useState(DEFAULT_PIXELS_PER_HOUR);
  const [viewportSize, setViewportSize] = React.useState<EngineViewportSize>({ width: 0, height: 0 });
  const [scrollPosition, setScrollPosition] = React.useState({ left: 0, top: 0 });
  const [previewSpans, setPreviewSpans] = React.useState(new Map<string, { start: number; end: number }>());
  const [taskTableOpen, setTaskTableOpen] = React.useState(false);
  const [selectionDrawerOpen, setSelectionDrawerOpen] = React.useState(false);
  const [fullscreen, setFullscreen] = React.useState(false);
  const [compressEmptyNights, setCompressEmptyNights] = React.useState(false);
  const [hideEmptyRows, setHideEmptyRows] = React.useState(false);
  const [undoStack, setUndoStack] = React.useState<GanttV5Transaction[]>([]);
  const [contextMenu, setContextMenu] = React.useState<{
    visible: boolean;
    x: number;
    y: number;
    taskId?: string;
    groupId?: string;
    rowId?: string;
    absoluteHour?: number;
    domainContext?: Record<string, unknown>;
    contextType: GanttV5ContextMenuContext['contextType'];
    selectedTaskIds: string[];
  }>({ visible: false, x: 0, y: 0, contextType: 'background', selectedTaskIds: [] });
  const mutationPending = interaction.transaction?.status === 'pending';

  React.useEffect(() => {
    onMutationPendingChange?.(mutationPending);
  }, [mutationPending, onMutationPendingChange]);

  React.useEffect(() => setDisplayTasks(tasks), [tasks]);
  React.useEffect(() => {
    if (controlledViewMode) setViewMode(controlledViewMode);
  }, [controlledViewMode]);

  const model = React.useMemo(() => buildGanttV5Model({
    tasks: displayTasks,
    groups,
    collapsedGroupIds: interaction.collapsedGroupIds,
    hideEmptyRows,
  }), [displayTasks, groups, hideEmptyRows, interaction.collapsedGroupIds]);
  const range = React.useMemo(() => timeRange ?? inferGanttV5TimeRange(displayTasks), [displayTasks, timeRange]);
  const todayHour = React.useMemo(() => {
    if (!timelineOriginDate) return undefined;
    const origin = new Date(timelineOriginDate.length === 10 ? `${timelineOriginDate}T00:00:00` : timelineOriginDate);
    if (Number.isNaN(origin.getTime())) return undefined;
    return (Date.now() - origin.getTime()) / 3_600_000;
  }, [timelineOriginDate]);
  const scale = React.useMemo(() => createGanttV5TimeScale({
    ...range,
    pixelsPerHour,
    minimumWidth: viewportSize.width,
    tasks: displayTasks,
    compressEmptyNights,
  }), [compressEmptyNights, displayTasks, pixelsPerHour, range, viewportSize.width]);
  const ticks = React.useMemo(
    () => buildGanttV5Ticks(scale, viewMode, timelineOriginDate),
    [scale, timelineOriginDate, viewMode],
  );
  const viewport: GanttV5Viewport = React.useMemo(() => ({
    width: viewportSize.width,
    height: viewportSize.height,
    scrollLeft: scrollPosition.left,
    scrollTop: scrollPosition.top,
  }), [scrollPosition.left, scrollPosition.top, viewportSize.height, viewportSize.width]);
  const visibleRows = React.useMemo(() => getGanttV5VisibleRows({
    rows: model.rows,
    scrollTop: scrollPosition.top,
    viewportHeight: Math.max(0, viewportSize.height - GANTT_V5_HEADER_HEIGHT),
    rowHeight,
  }), [model.rows, rowHeight, scrollPosition.top, viewportSize.height]);

  const measureViewport = React.useCallback(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;
    const width = scrollElement.clientWidth;
    const height = scrollElement.clientHeight;
    // A freshly mounted workbench can briefly inherit a zero-sized ancestor
    // while a lazy route or view switch settles. Keep the last valid size and
    // remeasure on the following frames instead of committing an empty canvas.
    if (width <= 0 || height <= 0) return;
    setViewportSize((current) => (
      current.width === width && current.height === height
        ? current
        : { width, height }
    ));
  }, []);

  React.useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    root.style.setProperty('--gantt-v5-sidebar-width', `${sidebarWidth}px`);
    root.style.setProperty('--gantt-v5-row-height', `${rowHeight}px`);
  }, [rowHeight, sidebarWidth]);

  React.useLayoutEffect(() => {
    const surface = scrollSurfaceRef.current;
    if (!surface) return;
    surface.style.setProperty('--gantt-v5-content-width', `${Math.max(scale.totalWidth, viewportSize.width)}px`);
    surface.style.setProperty(
      '--gantt-v5-content-height',
      `${Math.max(viewportSize.height, model.rows.length * rowHeight + GANTT_V5_HEADER_HEIGHT)}px`,
    );
  }, [model.rows.length, rowHeight, scale.totalWidth, viewportSize.height, viewportSize.width]);

  React.useLayoutEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;
    measureViewport();
    const firstFrame = window.requestAnimationFrame(() => {
      measureViewport();
      resizeFrameRef.current = window.requestAnimationFrame(measureViewport);
    });
    window.addEventListener('resize', measureViewport);
    document.addEventListener('fullscreenchange', measureViewport);
    const observer = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(measureViewport);
    observer?.observe(scrollElement);
    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (resizeFrameRef.current !== null) {
        window.cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }
      window.removeEventListener('resize', measureViewport);
      document.removeEventListener('fullscreenchange', measureViewport);
      observer?.disconnect();
    };
  }, [measureViewport, model.rows.length]);

  React.useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(measureViewport);
    return () => window.cancelAnimationFrame(frame);
  }, [measureViewport, model.rows.length]);

  React.useEffect(() => () => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
  }, []);

  React.useEffect(() => {
    const handleFullscreenChange = () => setFullscreen(document.fullscreenElement === rootRef.current);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const publishSelection = React.useCallback((nextTaskIds: string[]) => {
    onSelectionChange?.(nextTaskIds);
  }, [onSelectionChange]);

  React.useEffect(() => {
    if (historyScopeRef.current === historyScopeKey) return;
    historyScopeRef.current = historyScopeKey;
    pointerSessionRef.current = null;
    retryRef.current = null;
    setPreviewSpans(new Map());
    setUndoStack([]);
    setContextMenu((current) => ({ ...current, visible: false, selectedTaskIds: [] }));
    dispatch({ type: 'CLEAR_SELECTED' });
    dispatch({ type: 'FOCUS_TASK', taskId: null });
    dispatch({ type: 'HOVER_TASK', taskId: null });
    if (interaction.transaction) {
      dispatch({ type: 'CLEAR_TRANSACTION', id: interaction.transaction.id });
    }
    publishSelection([]);
  }, [historyScopeKey, interaction.transaction, publishSelection]);

  const defaultCollapseScopeKey = React.useMemo(() => (
    `${historyScopeKey}:${groups.map((group) => `${group.id}:${group.collapsed ? 1 : 0}`).join('|')}`
  ), [groups, historyScopeKey]);

  React.useEffect(() => {
    if (defaultCollapseScopeRef.current === defaultCollapseScopeKey) return;
    defaultCollapseScopeRef.current = defaultCollapseScopeKey;
    const allIds = groups.map((group) => group.id);
    const collapsedIds = groups.filter((group) => group.collapsed).map((group) => group.id);
    dispatch({ type: 'SET_ALL_GROUPS_COLLAPSED', groupIds: allIds, collapsed: false });
    if (collapsedIds.length > 0) {
      dispatch({ type: 'SET_ALL_GROUPS_COLLAPSED', groupIds: collapsedIds, collapsed: true });
    }
  }, [defaultCollapseScopeKey, groups]);

  const selectTask = React.useCallback((taskId: string, additive: boolean) => {
    const next = additive
      ? (() => {
        const value = new Set(interaction.selectedTaskIds);
        if (value.has(taskId)) value.delete(taskId);
        else value.add(taskId);
        return Array.from(value);
      })()
      : [taskId];
    dispatch(additive ? { type: 'TOGGLE_SELECTED', taskId } : { type: 'SELECT_ONLY', taskId });
    const task = model.taskById.get(taskId);
    if (task) onTaskClick?.(task);
    // Some business adapters use onTaskClick to update the inspector with a
    // single task. Publish the authoritative engine selection afterwards so an
    // additive click cannot be collapsed back to one task by that callback.
    publishSelection(next);
  }, [interaction.selectedTaskIds, model.taskById, onTaskClick, publishSelection]);

  const toggleGroup = React.useCallback((groupId: string, currentlyExpanded: boolean) => {
    const collapsed = currentlyExpanded;
    dispatch({ type: 'SET_GROUP_COLLAPSED', groupId, collapsed });
    onGroupToggle?.(groupId, collapsed);
  }, [onGroupToggle]);

  const updateScrollPosition = React.useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    setScrollPosition({ left: element.scrollLeft, top: element.scrollTop });
  }, []);

  const handleScroll = React.useCallback(() => {
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      updateScrollPosition();
    });
  }, [updateScrollPosition]);

  const createResizeDraft = React.useCallback((taskId: string, edge: 'start' | 'end', deltaHours: number): GanttV5MoveDraft | null => {
    const task = model.taskById.get(taskId);
    if (!task || task.readOnly || task.resizable === false) return null;
    const delta = Math.round(deltaHours * 4) / 4;
    const minimumDuration = 0.25;
    const start = edge === 'start' ? Math.min(task.end - minimumDuration, task.start + delta) : task.start;
    const end = edge === 'end' ? Math.max(task.start + minimumDuration, task.end + delta) : task.end;
    return {
      deltaHours: delta,
      originals: [{ taskId, start: task.start, end: task.end }],
      updates: [{ taskId, start, end }],
      violations: [],
    };
  }, [model.taskById]);

  const handlePointerDown = React.useCallback((
    event: React.PointerEvent<HTMLCanvasElement>,
    hit: { rect: { taskId: string }; edge: 'start' | 'body' | 'end' } | null,
    groupHit: { groupId: string; taskIds: string[] } | null,
  ) => {
    if (!hit && !groupHit) {
      dispatch({ type: 'CLEAR_SELECTED' });
      publishSelection([]);
      return;
    }
    if (mutationPending) {
      event.preventDefault();
      dispatch({ type: 'ANNOUNCE', message: '上一项时间调整正在保存，请稍候' });
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    if (groupHit && !hit) {
      const selectableTaskIds = groupHit.taskIds.filter((taskId) => {
        const task = model.taskById.get(taskId);
        return Boolean(task && !task.readOnly && task.draggable !== false);
      });
      if (selectableTaskIds.length === 0) return;
      dispatch({ type: 'SET_SELECTED', taskIds: selectableTaskIds });
      publishSelection(selectableTaskIds);
      pointerSessionRef.current = {
        pointerId: event.pointerId,
        taskId: selectableTaskIds[0],
        edge: 'body',
        startClientX: event.clientX,
        draft: null,
        affectedTaskIds: selectableTaskIds,
        groupId: groupHit.groupId,
      };
      return;
    }
    if (!hit) return;
    if (!interaction.selectedTaskIds.has(hit.rect.taskId)) {
      selectTask(hit.rect.taskId, event.metaKey || event.ctrlKey || event.shiftKey);
    }
    pointerSessionRef.current = {
      pointerId: event.pointerId,
      taskId: hit.rect.taskId,
      edge: hit.edge,
      startClientX: event.clientX,
      draft: null,
      affectedTaskIds: interaction.selectedTaskIds.has(hit.rect.taskId)
        ? Array.from(interaction.selectedTaskIds)
        : [hit.rect.taskId],
    };
  }, [interaction.selectedTaskIds, model.taskById, mutationPending, publishSelection, selectTask]);

  const handlePointerMove = React.useCallback((
    event: React.PointerEvent<HTMLCanvasElement>,
    hit: { rect: { taskId: string } } | null,
  ) => {
    const session = pointerSessionRef.current;
    if (!session || session.pointerId !== event.pointerId || readOnly) {
      dispatch({ type: 'HOVER_TASK', taskId: hit?.rect.taskId ?? null });
      return;
    }
    const deltaPixels = event.clientX - session.startClientX;
    if (Math.abs(deltaPixels) < 3) return;
    const anchorTask = model.taskById.get(session.taskId);
    const anchorHour = session.edge === 'end' ? anchorTask?.end : anchorTask?.start;
    const deltaHours = scale.deltaPixelsToHours(deltaPixels, anchorHour);
    const selectedIds = session.affectedTaskIds;
    const draft = session.edge === 'body'
      ? createGanttV5MoveDraft({ tasks: model.taskById, taskIds: selectedIds, deltaHours })
      : createResizeDraft(session.taskId, session.edge, deltaHours);
    session.draft = draft;
    setPreviewSpans(new Map((draft?.updates ?? []).map((span) => [span.taskId, span])));
  }, [createResizeDraft, model.taskById, readOnly, scale]);

  const persistSpans = React.useCallback(async (
    kind: Exclude<GanttV5Transaction['kind'], 'undo'>,
    spans: GanttV5TaskSpan[],
  ) => {
    if (spans.length === 0) return;
    if (kind === 'task-resize') {
      if (!onTaskResize) throw new Error('当前业务未提供时长保存能力');
      await onTaskResize(spans[0]);
      return;
    }
    if (kind === 'group-move' || kind === 'multi-move') {
      if (!onTasksMove) throw new Error('当前业务未提供原子批量保存能力');
      await onTasksMove(spans);
      return;
    }
    if (!onTaskMove) throw new Error('当前业务未提供时间保存能力');
    await onTaskMove(spans[0]);
  }, [onTaskMove, onTaskResize, onTasksMove]);

  const persistDraft = React.useCallback(async (
    draft: GanttV5MoveDraft,
    edge: PointerSession['edge'],
    groupId?: string,
  ) => {
    if (draft.updates.length === 0) return;
    const kind = groupId
      ? 'group-move'
      : edge === 'body'
      ? (draft.updates.length > 1 ? 'multi-move' : 'task-move')
      : 'task-resize';
    const transaction = {
      ...createGanttV5Transaction(kind, draft),
      groupId,
      deltaHours: draft.deltaHours,
    };
    const run = async () => {
      dispatch({ type: 'BEGIN_TRANSACTION', transaction });
      setDisplayTasks((current) => applyGanttV5TaskSpans(current, draft.updates));
      try {
        if (groupId && onGroupMove) {
          await onGroupMove(groupId, draft.deltaHours, draft.updates);
        } else if (edge !== 'body') {
          await persistSpans('task-resize', draft.updates);
        } else if (draft.updates.length > 1 && onTasksMove) {
          await persistSpans('multi-move', draft.updates);
        } else {
          await persistSpans('task-move', draft.updates);
        }
        dispatch({ type: 'COMMIT_TRANSACTION', id: transaction.id });
        setUndoStack((current) => [...current, transaction].slice(-50));
        retryRef.current = null;
      } catch (error) {
        const message = error instanceof Error ? error.message : '保存失败';
        setDisplayTasks((current) => applyGanttV5TaskSpans(current, draft.originals));
        dispatch({ type: 'FAIL_TRANSACTION', id: transaction.id, error: message });
        throw error;
      }
    };
    retryRef.current = run;
    await run();
  }, [onGroupMove, onTasksMove, persistSpans]);

  const undoLatest = React.useCallback(() => {
    const committed = undoStack[undoStack.length - 1];
    if (!committed || committed.kind === 'undo' || interaction.transaction?.status === 'pending') return;
    const committedKind: Exclude<GanttV5Transaction['kind'], 'undo'> = committed.kind;
    const reversal = createGanttV5Transaction('undo', {
      originals: committed.updates,
      updates: committed.originals,
    });
    const run = async () => {
      dispatch({ type: 'BEGIN_TRANSACTION', transaction: reversal });
      try {
        if (committedKind === 'group-move' && !onTasksMove && committed.groupId && onGroupMove) {
          await onGroupMove(committed.groupId, -(committed.deltaHours ?? 0), committed.originals);
        } else {
          await persistSpans(committedKind, committed.originals);
        }
        setDisplayTasks((current) => applyGanttV5TaskSpans(current, committed.originals));
        setUndoStack((current) => current.filter((transaction) => transaction.id !== committed.id));
        dispatch({ type: 'COMMIT_TRANSACTION', id: reversal.id });
        dispatch({ type: 'ANNOUNCE', message: `已撤销 ${committed.originals.length} 个任务的时间调整` });
        retryRef.current = null;
      } catch (error) {
        const message = error instanceof Error ? error.message : '撤销保存失败';
        dispatch({ type: 'FAIL_TRANSACTION', id: reversal.id, error: message });
        throw error;
      }
    };
    retryRef.current = run;
    void run().catch(() => undefined);
  }, [interaction.transaction?.status, onGroupMove, onTasksMove, persistSpans, undoStack]);

  const handlePointerUp = React.useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    const session = pointerSessionRef.current;
    pointerSessionRef.current = null;
    setPreviewSpans(new Map());
    if (!session || session.pointerId !== event.pointerId || !session.draft) return;
    void persistDraft(session.draft, session.edge, session.groupId).catch(() => undefined);
  }, [persistDraft]);

  const handleDoubleClick = React.useCallback((
    event: React.MouseEvent<HTMLCanvasElement>,
    hit: { rect: { taskId: string } } | null,
  ) => {
    if (!hit) {
      const bounds = event.currentTarget.getBoundingClientRect();
      const hour = scale.xToHour(scrollPosition.left + event.clientX - bounds.left);
      const nextPixelsPerHour = clampGanttV5Zoom(Math.max(pixelsPerHour, 12));
      setViewMode('day');
      setPixelsPerHour(nextPixelsPerHour);
      requestAnimationFrame(() => {
        if (!scrollRef.current) return;
        const nextScale = createGanttV5TimeScale({
          ...range,
          pixelsPerHour: nextPixelsPerHour,
          minimumWidth: viewportSize.width,
          tasks: displayTasks,
          compressEmptyNights,
        });
        scrollRef.current.scrollLeft = Math.max(
          0,
          nextScale.hourToX(hour) - scrollRef.current.clientWidth / 2,
        );
        updateScrollPosition();
      });
      dispatch({ type: 'ANNOUNCE', message: `已聚焦 ${hour.toFixed(1)} 小时` });
      return;
    }
    if (mutationPending) {
      dispatch({ type: 'ANNOUNCE', message: '时间调整正在保存，暂不能编辑任务' });
      return;
    }
    const task = model.taskById.get(hit.rect.taskId);
    if (task) onTaskDoubleClick?.(task);
  }, [compressEmptyNights, displayTasks, model.taskById, mutationPending, onTaskDoubleClick, pixelsPerHour, range, scale, scrollPosition.left, updateScrollPosition, viewportSize.width]);

  const handleContextMenu = React.useCallback((
    event: React.MouseEvent<HTMLCanvasElement>,
    hit: { rect: { taskId: string } } | null,
    groupHit: { groupId: string; taskIds: string[] } | null,
  ) => {
    if (!contextMenuItems) return;
    event.preventDefault();
    if (mutationPending) {
      dispatch({ type: 'ANNOUNCE', message: '时间调整正在保存，暂不能打开写操作菜单' });
      return;
    }
    const bounds = event.currentTarget.getBoundingClientRect();
    const localX = event.clientX - bounds.left;
    const localY = event.clientY - bounds.top;
    const rowIndex = Math.floor((localY - GANTT_V5_HEADER_HEIGHT + scrollPosition.top) / rowHeight);
    const row = rowIndex >= 0 ? model.rows[rowIndex] : undefined;
    let selectedTaskIds: string[] = Array.from(interaction.selectedTaskIds);
    let taskId: string | undefined;
    let groupId: string | undefined = groupHit?.groupId ?? row?.groupId;
    let contextType: GanttV5ContextMenuContext['contextType'] = 'background';
    if (hit) {
      taskId = hit.rect.taskId;
      contextType = 'task';
      selectedTaskIds = interaction.selectedTaskIds.has(taskId)
        ? Array.from(interaction.selectedTaskIds)
        : [taskId];
      dispatch({ type: 'SET_SELECTED', taskIds: selectedTaskIds });
      const task = model.taskById.get(taskId);
      if (task) onTaskClick?.(task);
      publishSelection(selectedTaskIds);
    } else if (groupHit) {
      groupId = groupHit.groupId;
      contextType = 'group';
      selectedTaskIds = groupHit.taskIds;
      dispatch({ type: 'SET_SELECTED', taskIds: selectedTaskIds });
      publishSelection(selectedTaskIds);
    } else if (row?.groupId) {
      contextType = 'group';
      groupId = row.groupId;
    }
    const task = taskId ? model.taskById.get(taskId) : undefined;
    const representativeTaskId = taskId ?? (groupId
      ? model.descendantTaskIdsByGroupId.get(groupId)?.[0]
      : row?.taskId);
    const representativeTask = representativeTaskId ? model.taskById.get(representativeTaskId) : undefined;
    setContextMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      taskId,
      groupId,
      rowId: row?.id,
      absoluteHour: scale.xToHour(scrollPosition.left + localX),
      domainContext: {
        ...(representativeTask?.data ?? task?.data ?? {}),
        rowType: row?.type,
        groupType: groupId ? model.groupById.get(groupId)?.type : undefined,
      },
      contextType,
      selectedTaskIds,
    });
  }, [contextMenuItems, interaction.selectedTaskIds, model, mutationPending, onTaskClick, publishSelection, rowHeight, scale, scrollPosition.left, scrollPosition.top]);

  const focusTaskAt = React.useCallback((taskId: string) => {
    const rowIndex = model.taskRowById.get(taskId);
    const scroll = scrollRef.current;
    if (rowIndex === undefined || !scroll) return;
    const rowTop = rowIndex * rowHeight;
    const bodyHeight = viewportSize.height - GANTT_V5_HEADER_HEIGHT;
    if (rowTop < scroll.scrollTop) scroll.scrollTop = rowTop;
    else if (rowTop + rowHeight > scroll.scrollTop + bodyHeight) {
      scroll.scrollTop = rowTop + rowHeight - bodyHeight;
    }
    dispatch({ type: 'FOCUS_TASK', taskId });
  }, [model.taskRowById, rowHeight, viewportSize.height]);

  const handleKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLCanvasElement>) => {
    const orderedTaskIds = model.rows.flatMap((row) => (
      row.taskId ? [row.taskId] : row.inlineTaskIds
    ));
    if (orderedTaskIds.length === 0) return;
    const currentIndex = interaction.focusedTaskId
      ? orderedTaskIds.indexOf(interaction.focusedTaskId)
      : -1;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      focusTaskAt(orderedTaskIds[Math.min(orderedTaskIds.length - 1, currentIndex + 1)]);
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      focusTaskAt(orderedTaskIds[Math.max(0, currentIndex <= 0 ? 0 : currentIndex - 1)]);
    } else if (event.key === 'Enter' && interaction.focusedTaskId) {
      if (mutationPending) {
        event.preventDefault();
        dispatch({ type: 'ANNOUNCE', message: '时间调整正在保存，暂不能编辑任务' });
        return;
      }
      const task = model.taskById.get(interaction.focusedTaskId);
      if (task) onTaskDoubleClick?.(task);
    } else if (event.key === 'Delete' && interaction.focusedTaskId && !readOnly && onDeleteTask) {
      event.preventDefault();
      if (mutationPending) {
        dispatch({ type: 'ANNOUNCE', message: '时间调整正在保存，暂不能删除任务' });
        return;
      }
      const task = model.taskById.get(interaction.focusedTaskId);
      if (task && !task.readOnly && task.deletable !== false) onDeleteTask(task);
    } else if (
      (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10'))
      && interaction.focusedTaskId
      && contextMenuItems
    ) {
      event.preventDefault();
      if (mutationPending) {
        dispatch({ type: 'ANNOUNCE', message: '时间调整正在保存，暂不能打开写操作菜单' });
        return;
      }
      const bounds = event.currentTarget.getBoundingClientRect();
      const taskId = interaction.focusedTaskId;
      const task = model.taskById.get(taskId);
      const rowIndex = model.taskRowById.get(taskId);
      const row = rowIndex === undefined ? undefined : model.rows[rowIndex];
      dispatch({ type: 'SET_SELECTED', taskIds: [taskId] });
      publishSelection([taskId]);
      setContextMenu({
        visible: true,
        x: bounds.left + Math.min(bounds.width - 24, Math.max(24, bounds.width / 2)),
        y: bounds.top + Math.min(bounds.height - 24, Math.max(56, bounds.height / 3)),
        taskId,
        groupId: task?.groupId,
        rowId: row?.id,
        absoluteHour: task ? (task.start + task.end) / 2 : undefined,
        domainContext: {
          ...(task?.data ?? {}),
          rowType: row?.type,
          groupType: task?.groupId ? model.groupById.get(task.groupId)?.type : undefined,
        },
        contextType: 'task',
        selectedTaskIds: [taskId],
      });
    } else if (event.key === ' ' && interaction.focusedTaskId) {
      event.preventDefault();
      selectTask(interaction.focusedTaskId, event.metaKey || event.ctrlKey || event.shiftKey);
    } else if (event.key === 'Escape') {
      dispatch({ type: 'CLEAR_SELECTED' });
      publishSelection([]);
    } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
      event.preventDefault();
      undoLatest();
    }
  }, [contextMenuItems, focusTaskAt, interaction.focusedTaskId, model.groupById, model.rows, model.taskById, model.taskRowById, mutationPending, onDeleteTask, onTaskDoubleClick, publishSelection, readOnly, selectTask, undoLatest]);

  const handleWheel = React.useCallback((event: React.WheelEvent<HTMLCanvasElement>) => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    event.preventDefault();
    if (event.shiftKey && Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      scroll.scrollLeft += event.deltaY;
    } else {
      scroll.scrollLeft += event.deltaX;
      scroll.scrollTop += event.deltaY;
    }
  }, []);

  const setZoom = React.useCallback((next: number) => {
    const scroll = scrollRef.current;
    const oldScale = scale;
    const anchorX = scroll ? scroll.scrollLeft + scroll.clientWidth / 2 : 0;
    const anchorHour = oldScale.xToHour(anchorX);
    const clamped = clampGanttV5Zoom(next);
    setPixelsPerHour(clamped);
    requestAnimationFrame(() => {
      if (!scroll) return;
      const nextScale = createGanttV5TimeScale({
        ...range,
        pixelsPerHour: clamped,
        minimumWidth: viewportSize.width,
        tasks: displayTasks,
        compressEmptyNights,
      });
      const nextAnchorX = nextScale.hourToX(anchorHour);
      scroll.scrollLeft = Math.max(0, nextAnchorX - scroll.clientWidth / 2);
    });
  }, [compressEmptyNights, displayTasks, range, scale, viewportSize.width]);

  const fitRange = React.useCallback(() => {
    const unitScale = createGanttV5TimeScale({
      ...range,
      pixelsPerHour: 1,
      tasks: displayTasks,
      compressEmptyNights,
    });
    const displayedDuration = Math.max(1, unitScale.hourToX(range.end) - unitScale.hourToX(range.start));
    setPixelsPerHour(clampGanttV5Zoom(Math.max(1, viewportSize.width) / displayedDuration, 1, 48));
    requestAnimationFrame(() => {
      if (!scrollRef.current) return;
      scrollRef.current.scrollLeft = 0;
      updateScrollPosition();
    });
  }, [compressEmptyNights, displayTasks, range, updateScrollPosition, viewportSize.width]);

  React.useEffect(() => {
    if (!autoFitOnScopeChange || viewportSize.width <= 0 || displayTasks.length === 0) return;
    const scopeKey = `${historyScopeKey}:${range.start}:${range.end}:${Math.round(viewportSize.width)}`;
    if (autoFitScopeRef.current === scopeKey) return;
    autoFitScopeRef.current = scopeKey;
    fitRange();
  }, [autoFitOnScopeChange, displayTasks.length, fitRange, historyScopeKey, range.end, range.start, viewportSize.width]);

  const fitSelection = React.useCallback(() => {
    const selected = Array.from(interaction.selectedTaskIds)
      .map((taskId) => model.taskById.get(taskId))
      .filter((task): task is GanttV5EngineProps['tasks'][number] => Boolean(task));
    if (selected.length === 0) return;
    const selectedStart = Math.min(...selected.map((task) => task.start));
    const selectedEnd = Math.max(...selected.map((task) => task.end));
    const padding = Math.max(2, (selectedEnd - selectedStart) * 0.08);
    const selectionRange = { start: selectedStart - padding, end: selectedEnd + padding };
    const nextPixelsPerHour = getGanttV5FitPixelsPerHour(selectionRange, viewportSize.width);
    setPixelsPerHour(nextPixelsPerHour);
    requestAnimationFrame(() => {
      if (!scrollRef.current) return;
      const nextScale = createGanttV5TimeScale({
        ...range,
        pixelsPerHour: nextPixelsPerHour,
        minimumWidth: viewportSize.width,
        tasks: displayTasks,
        compressEmptyNights,
      });
      scrollRef.current.scrollLeft = Math.max(0, nextScale.hourToX(selectionRange.start));
    });
  }, [compressEmptyNights, displayTasks, interaction.selectedTaskIds, model.taskById, range, viewportSize.width]);

  const displayedRangeWidth = scale.hourToX(range.end) - scale.hourToX(range.start);
  const navigatorMinimum = viewportSize.width >= displayedRangeWidth
    ? (range.start + range.end) / 2
    : scale.xToHour(scale.hourToX(range.start) + viewportSize.width / 2);
  const navigatorMaximum = viewportSize.width >= displayedRangeWidth
    ? navigatorMinimum
    : scale.xToHour(scale.hourToX(range.end) - viewportSize.width / 2);
  const navigatorHour = Math.max(
    navigatorMinimum,
    Math.min(navigatorMaximum, scale.xToHour(scrollPosition.left + viewportSize.width / 2)),
  );
  const navigatorLabel = React.useMemo(() => {
    if (!timelineOriginDate) return `${navigatorHour.toFixed(1)} h`;
    const origin = new Date(timelineOriginDate);
    if (Number.isNaN(origin.getTime())) return `${navigatorHour.toFixed(1)} h`;
    const value = new Date(origin.getTime() + navigatorHour * 60 * 60 * 1000);
    return new Intl.DateTimeFormat('zh-CN', {
      month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(value);
  }, [navigatorHour, timelineOriginDate]);

  const navigateToHour = React.useCallback((hour: number) => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    scroll.scrollLeft = Math.max(0, scale.hourToX(hour) - scroll.clientWidth / 2);
    updateScrollPosition();
  }, [scale, updateScrollPosition]);

  const retryTransaction = React.useCallback(() => {
    void retryRef.current?.().catch(() => undefined);
  }, []);

  const dismissTransaction = React.useCallback(() => {
    if (!interaction.transaction) return;
    dispatch({ type: 'CLEAR_TRANSACTION', id: interaction.transaction.id });
    retryRef.current = null;
  }, [interaction.transaction]);

  const toggleFullscreen = React.useCallback(async () => {
    if (document.fullscreenElement === rootRef.current) {
      await document.exitFullscreen();
      return;
    }
    await rootRef.current?.requestFullscreen();
  }, []);

  const allGroupIds = React.useMemo(() => groups.map((group) => group.id), [groups]);
  const selectedCount = interaction.selectedTaskIds.size;
  const contextMenuContext = React.useMemo<GanttV5ContextMenuContext>(() => ({
    contextType: contextMenu.contextType,
    task: contextMenu.taskId ? model.taskById.get(contextMenu.taskId) ?? null : null,
    taskId: contextMenu.taskId,
    groupId: contextMenu.groupId,
    rowId: contextMenu.rowId,
    absoluteHour: contextMenu.absoluteHour,
    x: contextMenu.x,
    y: contextMenu.y,
    selectedTaskIds: contextMenu.selectedTaskIds,
    domainContext: contextMenu.domainContext,
  }), [contextMenu, model.taskById]);
  const resolvedContextMenuItems = React.useMemo(() => (
    typeof contextMenuItems === 'function' ? contextMenuItems(contextMenuContext) : contextMenuItems ?? []
  ), [contextMenuContext, contextMenuItems]);
  const selectedTasks = React.useMemo(() => Array.from(interaction.selectedTaskIds)
    .map((taskId) => model.taskById.get(taskId))
    .filter((task): task is GanttV5EngineProps['tasks'][number] => Boolean(task)), [interaction.selectedTaskIds, model.taskById]);

  const handleContextMenuCommand = React.useCallback((key: string) => {
    if (key === 'gantt-clear-selection') {
      dispatch({ type: 'CLEAR_SELECTED' });
      publishSelection([]);
    } else if (key === 'gantt-select-all') {
      const taskIds = Array.from(model.taskById.keys());
      dispatch({ type: 'SET_SELECTED', taskIds });
      publishSelection(taskIds);
    } else if (key === 'gantt-select-group' && contextMenu.groupId) {
      const taskIds = model.descendantTaskIdsByGroupId.get(contextMenu.groupId) ?? [];
      const everySelected = taskIds.length > 0 && taskIds.every((taskId) => interaction.selectedTaskIds.has(taskId));
      const next = everySelected
        ? Array.from(interaction.selectedTaskIds).filter((taskId) => !taskIds.includes(taskId))
        : Array.from(new Set([...Array.from(interaction.selectedTaskIds), ...taskIds]));
      dispatch({ type: 'SET_SELECTED', taskIds: next });
      publishSelection(next);
    } else if (key === 'gantt-toggle-group' && contextMenu.groupId) {
      const row = model.rows.find((item) => item.groupId === contextMenu.groupId);
      if (row) toggleGroup(contextMenu.groupId, row.expanded);
    } else {
      onContextMenuAction?.(key, contextMenuContext);
    }
    setContextMenu((current) => ({ ...current, visible: false }));
  }, [contextMenu.groupId, contextMenuContext, interaction.selectedTaskIds, model.descendantTaskIdsByGroupId, model.rows, model.taskById, onContextMenuAction, publishSelection, toggleGroup]);

  return (
    <div ref={rootRef} className={classNames('wxb-gantt-v5', className)}>
      <div className="wxb-gantt-v5-toolbar" role="toolbar" aria-label="甘特图工具栏">
        <div className="wxb-gantt-v5-toolbar__leading">
          {toolbarLeading}
          <WxbTag color="blue">{displayTasks.length} 个任务</WxbTag>
          {selectedCount > 0 && (
            <WxbButton variant="ghost" size="sm" onClick={() => setSelectionDrawerOpen(true)}>
              已选 {selectedCount}
            </WxbButton>
          )}
        </div>
        <div className="wxb-gantt-v5-toolbar__actions">
          <WxbSegmented
            size="sm"
            aria-label="时间粒度"
            value={viewMode}
            onChange={(value) => setViewMode(value as GanttV5ViewMode)}
            options={[
              { label: '时', value: 'hour' },
              { label: '日', value: 'day' },
              { label: '周', value: 'week' },
              { label: '月', value: 'month' },
            ]}
          />
          <WxbButton variant="ghost" size="sm" aria-label="缩小时间轴" onClick={() => setZoom(pixelsPerHour / 1.25)}>
            <WxbIcon name="zoom-out" size={15} />
          </WxbButton>
          <WxbButton variant="ghost" size="sm" aria-label="放大时间轴" onClick={() => setZoom(pixelsPerHour * 1.25)}>
            <WxbIcon name="zoom-in" size={15} />
          </WxbButton>
          <WxbButton variant="secondary" size="sm" onClick={fitRange}>
            <WxbIcon name="gantt-bars" size={15} />
            适应范围
          </WxbButton>
          <WxbButton variant="ghost" size="sm" disabled={selectedCount === 0} onClick={fitSelection}>
            适应选择
          </WxbButton>
          <WxbTooltip title="撤销最近一次已保存的时间调整（Ctrl/Command + Z）">
            <WxbButton
              variant="ghost"
              size="sm"
              disabled={undoStack.length === 0 || interaction.transaction?.status === 'pending'}
              onClick={undoLatest}
            >
              撤销{undoStack.length > 0 ? ` ${undoStack.length}` : ''}
            </WxbButton>
          </WxbTooltip>
          <WxbActionMenu
            triggerLabel="视图设置"
            triggerAriaLabel="甘特视图设置"
            actions={[
              { key: 'expand-all', label: '展开全部分组', onClick: () => dispatch({ type: 'SET_ALL_GROUPS_COLLAPSED', groupIds: allGroupIds, collapsed: false }) },
              { key: 'collapse-all', label: '折叠全部分组', onClick: () => dispatch({ type: 'SET_ALL_GROUPS_COLLAPSED', groupIds: allGroupIds, collapsed: true }) },
              { key: 'compress-nights', label: compressEmptyNights ? '取消压缩空夜间' : '压缩空夜间', onClick: () => setCompressEmptyNights((current) => !current) },
              { key: 'hide-empty', label: hideEmptyRows ? '显示空分组行' : '隐藏空分组行', onClick: () => setHideEmptyRows((current) => !current) },
              { key: 'task-table', label: '打开任务表', onClick: () => setTaskTableOpen(true) },
              { key: 'fullscreen', label: fullscreen ? '退出全屏' : '进入全屏', onClick: () => void toggleFullscreen() },
            ]}
          />
          {toolbarActions}
        </div>
      </div>

      {model.diagnostics.length > 0 && (
        <WxbAlert title="甘特数据存在结构异常">
          已保留可显示数据；共发现 {model.diagnostics.length} 个重复、孤立或循环关系。
        </WxbAlert>
      )}
      {interaction.transaction?.status === 'failed' && (
        <WxbAlert variant="error" title="时间调整未保存">
          <span>{interaction.transaction.error}</span>
          <span className="wxb-gantt-v5-alert__actions">
            <WxbButton variant="secondary" size="sm" onClick={retryTransaction}>重试</WxbButton>
            <WxbButton variant="ghost" size="sm" onClick={dismissTransaction}>关闭</WxbButton>
          </span>
        </WxbAlert>
      )}

      {model.rows.length > 0 && viewportSize.width + 1 < displayedRangeWidth && (
        <div className="wxb-gantt-v5-navigator" aria-label="甘特缩略时间导航">
          <span>时间导航</span>
          <WxbSlider
            min={navigatorMinimum}
            max={navigatorMaximum}
            step={0.25}
            value={navigatorHour}
            showValue={false}
            aria-label="移动甘特可见时间范围"
            onChange={navigateToHour}
          />
          <span className="wxb-gantt-v5-navigator__value">{navigatorLabel}</span>
        </div>
      )}

      {model.rows.length === 0 ? (
        <WxbEmpty className="wxb-gantt-v5-empty" description={emptyDescription} />
      ) : (
        <div className="wxb-gantt-v5-workspace">
          <GanttV5Sidebar
            model={model}
            visibleRows={visibleRows}
            selectedTaskIds={interaction.selectedTaskIds}
            focusedTaskId={interaction.focusedTaskId}
            offsetPixels={visibleRows.offsetTop - scrollPosition.top}
            onGroupToggle={toggleGroup}
            onTaskSelect={selectTask}
          />
          <div className="wxb-gantt-v5-timeline">
            <div ref={scrollRef} className="wxb-gantt-v5-scroll" onScroll={handleScroll}>
              <div ref={scrollSurfaceRef} className="wxb-gantt-v5-scroll__surface" aria-hidden="true" />
            </div>
            <GanttV5CanvasLayers
              rootRef={rootRef}
              model={model}
              dependencies={dependencies}
              links={links}
              ticks={ticks}
              scale={scale}
              viewport={viewport}
              rowHeight={rowHeight}
              todayHour={todayHour}
              selectedTaskIds={interaction.selectedTaskIds}
              hoveredTaskId={interaction.hoveredTaskId}
              connectionVisibility={connectionVisibility}
              previewSpans={previewSpans}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={() => dispatch({ type: 'HOVER_TASK', taskId: null })}
              onDoubleClick={handleDoubleClick}
              onContextMenu={handleContextMenu}
              onKeyDown={handleKeyDown}
              onWheel={handleWheel}
              onLayerRender={onLayerRender}
            />
          </div>
        </div>
      )}
      <div className="wxb-gantt-v5-sr-only" aria-live="polite" aria-atomic="true">
        {interaction.announcement}
      </div>
      <GanttV5ContextMenu
        visible={contextMenu.visible && resolvedContextMenuItems.length > 0}
        x={contextMenu.x}
        y={contextMenu.y}
        task={contextMenuContext.task}
        items={resolvedContextMenuItems}
        selectedCount={contextMenu.selectedTaskIds.length}
        contextType={contextMenu.contextType}
        onAction={handleContextMenuCommand}
        onClose={() => setContextMenu((current) => ({ ...current, visible: false }))}
      />
      <WxbDrawer
        open={taskTableOpen}
        title="甘特任务表"
        width={720}
        onClose={() => setTaskTableOpen(false)}
      >
        <WxbDataTable
          rowKey="id"
          size="small"
          density="compact"
          pagination={{ pageSize: 50, showSizeChanger: true }}
          dataSource={displayTasks}
          rowSelection={{
            selectedRowKeys: Array.from(interaction.selectedTaskIds),
            preserveSelectedRowKeys: true,
            onChange: (keys) => {
              const taskIds = keys.map(String);
              dispatch({ type: 'SET_SELECTED', taskIds });
              publishSelection(taskIds);
            },
          }}
          columns={[
            { title: '任务', dataIndex: 'label', key: 'label' },
            { title: '开始（小时）', dataIndex: 'start', key: 'start' },
            { title: '结束（小时）', dataIndex: 'end', key: 'end' },
            { title: '执行状态', dataIndex: 'status', key: 'status', render: (value: unknown) => String(value ?? '—') },
            {
              title: '人员',
              key: 'people',
              render: (_value: unknown, task: GanttV5EngineProps['tasks'][number]) => (
                `${task.assignedPeople ?? 0} / ${task.requiredPeople ?? 0}`
              ),
            },
            {
              title: '操作',
              key: 'action',
              render: (_value: unknown, task: GanttV5EngineProps['tasks'][number]) => (
                <div className="wxb-gantt-v5-task-table__actions">
                  <WxbButton
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      selectTask(task.id, false);
                      focusTaskAt(task.id);
                      navigateToHour((task.start + task.end) / 2);
                      setTaskTableOpen(false);
                    }}
                  >
                    定位
                  </WxbButton>
                  {onTaskDoubleClick && (
                    <WxbButton
                      variant="secondary"
                      size="sm"
                      disabled={mutationPending}
                      onClick={() => {
                        selectTask(task.id, false);
                        setTaskTableOpen(false);
                        onTaskDoubleClick(task);
                      }}
                    >
                      编辑
                    </WxbButton>
                  )}
                  {!readOnly && !task.readOnly && onDeleteTask && task.deletable !== false && (
                    <WxbButton
                      variant="danger"
                      size="sm"
                      disabled={mutationPending}
                      onClick={() => {
                        selectTask(task.id, false);
                        setTaskTableOpen(false);
                        onDeleteTask(task);
                      }}
                    >
                      删除
                    </WxbButton>
                  )}
                </div>
              ),
            },
          ]}
          locale={{ emptyText: '当前没有任务' }}
        />
      </WxbDrawer>
      <SelectionDrawerV5
        open={selectionDrawerOpen}
        tasks={selectedTasks}
        onClose={() => setSelectionDrawerOpen(false)}
        actions={selectionActions?.(selectedTasks, () => setSelectionDrawerOpen(false))}
        onRemove={(taskId) => selectTask(taskId, true)}
        onClear={() => {
          dispatch({ type: 'CLEAR_SELECTED' });
          publishSelection([]);
        }}
      />
    </div>
  );
};
